import React, { useEffect, useState } from 'react';
import api from '../utils/api';

interface AnalyticsStats {
  totalOrders: number;
  deliveredOrders: number;
  netRevenue: number;
  totalPublishedProducts: number;
  totalUniqueVisitors: number;
  totalVisits: number;
}

const formatIlsCurrency = (amount: number) => {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const AdminAnalytics: React.FC = () => {
  const [stats, setStats] = useState<AnalyticsStats>({
    totalOrders: 0,
    deliveredOrders: 0,
    netRevenue: 0,
    totalPublishedProducts: 0,
    totalUniqueVisitors: 0,
    totalVisits: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/api/orders/admin/stats');
        setStats({
          totalOrders: data?.totalOrders || 0,
          deliveredOrders: data?.deliveredOrders || 0,
          netRevenue: data?.netRevenue || 0,
          totalPublishedProducts: data?.totalPublishedProducts || 0,
          totalUniqueVisitors: data?.totalUniqueVisitors || 0,
          totalVisits: data?.totalVisits || 0
        });
      } catch (error) {
        console.error('Error fetching analytics stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600 dark:text-dark-textSecondary">{'\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u062a\u062d\u0644\u064a\u0644\u0627\u062a...'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white dark:bg-dark-bg min-h-screen transition-colors duration-300" dir="rtl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">{'\u062a\u062d\u0644\u064a\u0644\u0627\u062a'}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">{'\u0639\u062f\u062f \u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0627\u0644\u062a\u064a \u062a\u0645 \u0637\u0644\u0628\u0647\u0627:'}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-text">{stats.totalOrders}</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">{'\u0639\u062f\u062f \u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0627\u0644\u062a\u064a \u062a\u0645 \u062a\u0633\u0644\u064a\u0645\u0647\u0627:'}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-text">{stats.deliveredOrders}</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">{'\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0631\u0628\u062d:'}</p>
          <p className="text-3xl font-bold text-pink-600 dark:text-pink-400">{formatIlsCurrency(stats.netRevenue)}</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">{'\u0639\u062f\u062f \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a \u0627\u0644\u0645\u0646\u0634\u0648\u0631\u0629:'}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-text">{stats.totalPublishedProducts}</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">عدد العارضين للموقع من دون تكرار:</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-text">{stats.totalUniqueVisitors}</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <p className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">عدد زيارات الموقع:</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-dark-text">{stats.totalVisits}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
